import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

/**
 * AgentStats — performance dashboard for a single agent.
 *
 * Lives at /agents/:id/stats. Fetches /api/rsi-agents/:id/stats?days=N
 * which aggregates over agent_runs + agent_run_events + (when present)
 * agent_corrections. Works for built-in and user-created agents
 * identically because the underlying tables key on agent_id strings.
 *
 * Renders:
 *   - 5 KPI tiles (runs, pass rate, avg runtime, low-confidence rows,
 *     corrections)
 *   - Confidence distribution bar (green/amber/rose stacked)
 *   - Recent runs table with per-run confidence chips + link to
 *     activity log
 *
 * Time window selector defaults to 30 days, options 7 / 30 / 90 / 365.
 */
export default function AgentStats() {
  const { id: agentId } = useParams();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStats(null); setErr(null);
    fetch(`/api/rsi-agents/${encodeURIComponent(agentId)}/stats?days=${days}`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : r.json().then((b) => Promise.reject(new Error(b.error || `HTTP ${r.status}`))))
      .then((data) => { if (!cancelled) setStats(data); })
      .catch((e) => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [agentId, days]);

  if (err) return <div className="max-w-5xl mx-auto p-8 text-rose-700">{err}</div>;
  if (!stats) return <div className="max-w-5xl mx-auto p-8 text-slate-500">Loading stats…</div>;

  const conf = stats.confidence_aggregate || { high: 0, medium: 0, low: 0 };
  const confTotal = (conf.high + conf.medium + conf.low) || 1;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <header className="mb-6 flex items-baseline justify-between gap-4">
        <div>
          <Link to={`/jobotics-agent?agentId=${encodeURIComponent(agentId)}`} className="text-xs text-slate-500 hover:text-slate-700">← Back to chat</Link>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">Performance</h1>
          <p className="text-xs text-slate-500 mt-0.5"><code>{agentId}</code></p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Window:</span>
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs px-2 py-1 rounded border ${days === d ? 'bg-teal-600 text-white border-teal-600' : 'bg-white border-slate-300 hover:border-slate-400 text-slate-700'}`}
            >
              {d === 365 ? '1y' : `${d}d`}
            </button>
          ))}
        </div>
      </header>

      {stats.total_runs === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-600">
          No runs in the last {days} days yet.
          {' '}<Link to={`/jobotics-agent?agentId=${encodeURIComponent(agentId)}`} className="text-teal-700 hover:underline">Open the chat</Link> to try this agent.
        </div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <KpiTile label="Runs" value={stats.total_runs.toLocaleString()} sub={`${stats.completed}✓ / ${stats.errored}✗${stats.orphaned ? ` / ${stats.orphaned} stalled` : ''}`} />
            <KpiTile
              label="Pass rate"
              value={stats.pass_rate != null ? `${Math.round(stats.pass_rate * 100)}%` : '—'}
              tone={stats.pass_rate != null && stats.pass_rate < 0.7 ? 'amber' : 'default'}
            />
            <KpiTile label="Avg runtime" value={fmtDuration(stats.avg_runtime_seconds)} sub={`median ${fmtDuration(stats.median_runtime_seconds)}`} />
            <KpiTile
              label="Low-confidence rows"
              value={conf.low.toLocaleString()}
              sub={`of ${confTotal.toLocaleString()} total`}
              tone={conf.low > 0 ? 'rose' : 'default'}
            />
            <KpiTile
              label="Corrections"
              value={stats.total_corrections == null ? '—' : stats.total_corrections.toLocaleString()}
              sub={stats.total_corrections == null ? 'not tracked yet' : `(coming with Phase D)`}
            />
          </div>

          {/* Confidence distribution bar */}
          {confTotal > 0 && (
            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-baseline justify-between mb-2">
                <h2 className="text-sm font-semibold text-slate-800">Output confidence — last {days}d</h2>
                <span className="text-xs text-slate-500">{confTotal.toLocaleString()} rows across {stats.completed} completed runs</span>
              </div>
              <div className="flex h-6 rounded overflow-hidden border border-slate-200">
                {conf.high > 0 && (
                  <div
                    className="bg-emerald-400 flex items-center justify-center text-[10px] font-medium text-white"
                    style={{ width: `${(conf.high / confTotal) * 100}%` }}
                    title={`${conf.high.toLocaleString()} high-confidence rows`}
                  >
                    {((conf.high / confTotal) * 100).toFixed(0)}%
                  </div>
                )}
                {conf.medium > 0 && (
                  <div
                    className="bg-amber-400 flex items-center justify-center text-[10px] font-medium text-white"
                    style={{ width: `${(conf.medium / confTotal) * 100}%` }}
                    title={`${conf.medium.toLocaleString()} medium-confidence rows`}
                  >
                    {((conf.medium / confTotal) * 100).toFixed(0)}%
                  </div>
                )}
                {conf.low > 0 && (
                  <div
                    className="bg-rose-400 flex items-center justify-center text-[10px] font-medium text-white"
                    style={{ width: `${(conf.low / confTotal) * 100}%` }}
                    title={`${conf.low.toLocaleString()} low-confidence rows`}
                  >
                    {((conf.low / confTotal) * 100).toFixed(0)}%
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/>High {conf.high.toLocaleString()}</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/>Medium {conf.medium.toLocaleString()}</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500"/>Low {conf.low.toLocaleString()}</span>
              </div>
            </section>
          )}

          {/* Recent runs */}
          <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-800">Recent runs</h2>
            </div>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-2 text-left">Started</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Took</th>
                  <th className="px-4 py-2 text-left">Confidence</th>
                  <th className="px-4 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.recent_runs.map((r) => {
                  const cs = r.confidence_summary;
                  return (
                    <tr key={r.run_id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-700 text-xs">{new Date(r.started_at).toLocaleString()}</td>
                      <td className="px-4 py-2"><RunStatusPill status={r.status}/></td>
                      <td className="px-4 py-2 text-slate-700 text-xs">{r.took_seconds != null ? fmtDuration(r.took_seconds) : '—'}</td>
                      <td className="px-4 py-2 text-xs">
                        {cs ? (
                          <span className="inline-flex items-center gap-1.5">
                            {cs.high > 0 && <span className="text-emerald-700">{cs.high} high</span>}
                            {cs.medium > 0 && <span className="text-amber-700">{cs.medium} med</span>}
                            {cs.low > 0 && <span className="text-rose-700">{cs.low} low</span>}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Link to={`/runs/${r.run_id}`} className="text-xs text-teal-700 hover:underline">view</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, tone = 'default' }) {
  const TONE = {
    default: 'bg-white border-slate-200',
    amber:   'bg-amber-50 border-amber-200',
    rose:    'bg-rose-50 border-rose-200',
  };
  return (
    <div className={`rounded-lg border ${TONE[tone] || TONE.default} px-4 py-3 shadow-sm`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-0.5 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RunStatusPill({ status }) {
  const tone = ({
    done: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    error: 'bg-rose-50 text-rose-800 border-rose-200',
    running: 'bg-amber-50 text-amber-800 border-amber-200',
    orphaned: 'bg-slate-100 text-slate-700 border-slate-300',
  })[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  const label = ({ done: 'completed', error: 'failed', running: 'in progress', orphaned: 'stalled' })[status] || status;
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
}

function fmtDuration(secs) {
  if (!secs || secs < 0) return '—';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}
